import { replace } from 'connected-react-router'
import {
  NEW_USER,
  LOAD_USERS,
  LOAD_USER_ROLES,
  GET_USER,
  NOTIFY_DANGER,
  NOTIFY_SUCCESS,
  NOTIFY_WARNING,
  DELETE_USER,
  CLEAR_USER,
  UPDATE_CURRENT_USER_PASSWORD,
  STORE_USERS_SEARCHINFO,
  STORE_USERS_ACTIVEINFO,
  STORE_USERS_LASTSORT,
  CLEAR_USER_SEARCH_INFO,
  UPDATE_USER,
} from './action-types'

import { request, getActiveUser } from './auth-actions'
import { UserDetailsModel } from '../../models/user'
import { Roles } from '../../models/roles'
import { notify } from './notification-actions'
import { getSelectedStores } from './store-info-actions'
import { genericError, saveUserError, silentError } from './action-utils'
import userServiceApi from '../../api/user-service-api'
import {
  createUserSaveRequest,
  createUserUpdateRequest,
  createUpdateMeRequest,
  createUserStoresUpdateRequest,
  createUserAuthoritiesUpdateRequest,
} from '../../api/request-builder/user-service-request-builder'
import authServiceApi from '../../api/auth-service-api'
import { getText } from '../../internal/TextLabels'
import PageParamBuilder from '../../higher-order/pageable-table/page-param-builder'
import { getDefaultForCountry } from '../../models/languages'

export const updateCurrentUserPasswordSuccess = data => ({
  type: UPDATE_CURRENT_USER_PASSWORD,
  data,
})

export const getUsersSuccess = response => ({ type: LOAD_USERS, response })

export const getUserRolesSuccess = response => ({
  type: LOAD_USER_ROLES,
  response,
})

export const deleteUserSuccess = id => ({ type: DELETE_USER, id })

export const getUserSuccess = response => ({ type: GET_USER, response })

export const updateUserSuccess = response => ({ type: UPDATE_USER, response })

export const newUserSuccess = user => ({ type: NEW_USER, user })

export const clearUser = () => ({ type: CLEAR_USER })

export const storeSearchInfo = info => ({ type: STORE_USERS_SEARCHINFO, info })

export const storeActiveInfo = info => ({ type: STORE_USERS_ACTIVEINFO, info })

export const storeLastSort = lastSort => ({
  type: STORE_USERS_LASTSORT,
  lastSort,
})

export const clearUserSearchInfo = () => ({
  type: CLEAR_USER_SEARCH_INFO,
})

export const newUser = () => (dispatch, getState) => {
  const user = new UserDetailsModel()
  let authorities = []

  user.languageCode = getDefaultForCountry(
    getState().storeInfo.currentRepresentative.country,
  )

  getState().users.validRoles.forEach(role => {
    if (
      role.name === Roles.STORE_SALES ||
      role.name === Roles.STORE_SALES_LIMITED
    ) {
      authorities = [...role.authorities]
    }
  })

  user.role = {
    name: Roles.STORE_SALES,
    authorities,
  }
  user.stores.push(getState().auth.store.storeUID)
  dispatch(newUserSuccess(user))
}

export const getUsers = (pageInfo, searchInfo, activeInfo) => dispatch => {
  // store current paging, search and ordering info
  if (searchInfo) {
    dispatch(storeSearchInfo(searchInfo))
  }
  if (activeInfo) {
    dispatch(storeActiveInfo(activeInfo))
  }
  if (pageInfo) {
    dispatch(storeLastSort(pageInfo.getSortListItemAsObject()))
  }

  return dispatch(
    request(userServiceApi.getUsers, { pageInfo, searchInfo, activeInfo }),
  )
    .then(data => {
      dispatch(getUsersSuccess(data))
    })
    .catch(error => {
      genericError(error)
      throw error
    })
}

export const saveUser = user => dispatch => {
  return dispatch(request(userServiceApi.saveUser, createUserSaveRequest(user)))
    .then(response => {
      dispatch(clearUser())
      dispatch(
        request(userServiceApi.updateUserAuthorities, {
          id: response.id,
          authorities: createUserAuthoritiesUpdateRequest(user),
        }),
      )
      return response
    })
    .then(response => {
      dispatch(newUser())

      notify(
        NOTIFY_SUCCESS,
        getText(
          'user.add.userSaved',
          user.firstName,
          user.lastName,
          user.username,
        ),
      )
      dispatch(getUsers())
      return response
    })
    .catch(error => {
      silentError(error)
      saveUserError(error)
      throw error
    })
}

export const updateProfile = user => dispatch => {
  return dispatch(
    request(userServiceApi.updateMe, {
      user: createUpdateMeRequest(user),
    }),
  )
    .then(() => {
      notify(NOTIFY_SUCCESS, getText('default.feedback.saved'))
      dispatch(getActiveUser())
    })
    .catch(error => {
      saveUserError(error)
      throw error
    })
}

export const editIncompleteUserProfile = user => dispatch => {
  return dispatch(
    request(userServiceApi.updateMe, {
      user: createUpdateMeRequest(user),
    }),
  )
    .then(() => {
      notify(NOTIFY_SUCCESS, getText('default.feedback.saved'))
      dispatch(getActiveUser()).then(() => {
        dispatch(replace('/users'))
      })
    })
    .catch(error => {
      genericError(error)
      throw error
    })
}

const getPageInfo = (pagination, sorting) => {
  return Object.assign(new PageParamBuilder(), {
    page: pagination.pageNumber,
    size: pagination.size,
    totalPages: pagination.totalPages,
    sortList:
      sorting && sorting.headerName !== ''
        ? [`${sorting.headerName},${sorting.direction}`]
        : ['username,asc'],
  })
}

export const getUser = id => dispatch => {
  return dispatch(request(userServiceApi.getUser, id))
    .then(data => {
      dispatch(getUserSuccess(data))
    })
    .catch(error => {
      silentError(error)
      genericError()
      throw error
    })
}

export const updateUser = user => (dispatch, getState) => {
  return dispatch(
    request(userServiceApi.updateUser, {
      id: user.id,
      user: createUserUpdateRequest(user),
    }),
  )
    .then(() => {
      dispatch(updateUserSuccess(user))
      return dispatch(
        request(userServiceApi.updateUserStores, {
          id: user.id,
          stores: createUserStoresUpdateRequest(user),
        }),
      ).then(() => {
        dispatch(getSelectedStores(user.id))
        return dispatch(
          request(userServiceApi.updateUserAuthorities, {
            id: user.id,
            authorities: createUserAuthoritiesUpdateRequest(user),
          }),
        ).then(() => {
          notify(NOTIFY_SUCCESS, getText('user.edit.updated'))
          dispatch(
            getUsers(
              getPageInfo(
                getState().users.pagination,
                getState().users.lastSort,
              ),
              getState().users.searchInfo,
              getState().users.activeInfo,
            ),
          )
          dispatch(getUser(user.id))
        })
      })
    })
    .catch(error => {
      silentError(error)
      saveUserError(error)
      throw error
    })
}

export const confirmEmailChange = changeRequest => dispatch => {
  return dispatch(request(userServiceApi.confirmEmailChange, changeRequest))
    .then(() => {
      notify(NOTIFY_SUCCESS, getText('default.feedback.saved'))
      dispatch(replace('/login'))
    })
    .catch(error => {
      genericError(error)
      throw error
    })
}

export const getUserRoles = () => dispatch => {
  return dispatch(request(userServiceApi.getRoles, null))
    .then(data => {
      dispatch(getUserRolesSuccess(data))
    })
    .catch(error => {
      genericError(error)
      throw error
    })
}

export const deleteUser = id => (dispatch, getState) => {
  return dispatch(request(userServiceApi.deleteUser, id))
    .then(data => {
      dispatch(deleteUserSuccess(data))
      notify(NOTIFY_SUCCESS, getText('user.delete.userDeleted'))
      dispatch(
        getUsers(
          getPageInfo(getState().users.pagination, getState().users.lastSort),
        ),
      )
      dispatch(clearUserSearchInfo())
    })
    .catch(error => {
      silentError(error)
      genericError()
      throw error
    })
}

export const addUser = () => dispatch => {
  dispatch(clearUser())

  dispatch(getUserRoles()).then(() => {
    dispatch(newUser())
  })
}

export const resetUser = () => dispatch => {
  dispatch(clearUser())
  dispatch(getUserRoles()).then(() => {
    dispatch(newUser())
  })
}

export const openUser = id => dispatch => {
  dispatch(clearUser())

  dispatch(getSelectedStores(id))
  dispatch(getUser(id))
}

export const updateCurrentUserPassword = passwords => dispatch => {
  return dispatch(request(authServiceApi.updatePassword, passwords))
    .then(() => {
      dispatch(updateCurrentUserPasswordSuccess())
      notify(NOTIFY_SUCCESS, getText('password.updated'))
    })
    .catch(error => {
      notify(NOTIFY_WARNING, getText('password.error.oldPasswordMismatch'))

      throw error
    })
}

const handleUserExportResult = promise =>
  promise
    .then(({ blob, filename }) => ({
      url: URL.createObjectURL(blob),
      size: blob.size,
      filename,
    }))
    .catch(error => {
      notify(NOTIFY_DANGER, getText('user.export.error'))
      throw error
    })

export const exportStoreUsers = () => dispatch =>
  handleUserExportResult(dispatch(request(userServiceApi.exportStoreUsers())))

export const exportRepresentativeUsers = () => dispatch =>
  handleUserExportResult(
    dispatch(request(userServiceApi.exportRepresentativeUsers())),
  )
